import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './OrderHistory.css';

const formatPrice = (p) => '৳' + Number(p).toLocaleString('en-BD');

const STATUS_META = {
  pending:   { label: 'Pending',   color: '#d97706', bg: '#fef3c7' },
  confirmed: { label: 'Confirmed', color: '#2563eb', bg: '#eff6ff' },
  shipped:   { label: 'Shipped',   color: '#7c3aed', bg: '#f5f3ff' },
  delivered: { label: 'Delivered', color: '#16a34a', bg: '#f0fdf4' },
  cancelled: { label: 'Cancelled', color: '#e63946', bg: '#fff5f5' },
};

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

const OrderHistory = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [inputPhone, setInputPhone] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchOrders = async (p) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/orders.php?phone=${encodeURIComponent(p)}`);
      const data = await res.json();
      if (data.success) {
        setOrders(data.orders);
        if (data.orders.length === 0) setError('No orders found for this number.');
      } else {
        setError(data.message || 'Failed to load orders.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = (e) => {
    e.preventDefault();
    if (!inputPhone.trim()) return;
    setPhone(inputPhone.trim());
    fetchOrders(inputPhone.trim());
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    setCancellingId(orderId);
    try {
      const res = await fetch(`${API_BASE}/orders.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId, status: 'cancelled' }),
      });
      const data = await res.json();
      if (data.success) {
        setOrders((prev) =>
          prev.map((o) => o.id === orderId ? { ...o, status: 'cancelled' } : o)
        );
      } else {
        alert(data.message || 'Failed to cancel order.');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="oh-page">
      <header className="oh-header">
        <button className="oh-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <span className="oh-header-title">My Orders</span>
      </header>

      <div className="oh-content">
        {/* Phone lookup */}
        <div className="oh-lookup">
          <div className="oh-lookup-title">📱 Enter your phone number to view orders</div>
          <form className="oh-lookup-form" onSubmit={handleLookup}>
            <input
              type="tel"
              value={inputPhone}
              onChange={(e) => setInputPhone(e.target.value)}
              placeholder="01XXXXXXXXX"
              className="oh-lookup-input"
            />
            <button type="submit" className="oh-lookup-btn" disabled={loading}>
              {loading ? '...' : 'Find Orders'}
            </button>
          </form>
        </div>

        {error && <div className="oh-msg oh-msg-error">{error}</div>}

        {orders.length > 0 && (
          <div className="oh-orders">
            <div className="oh-orders-count">{orders.length} order(s) for {phone}</div>
            {orders.map((order) => {
              const meta = STATUS_META[order.status] || STATUS_META.pending;
              const canCancel = ['pending', 'confirmed'].includes(order.status);
              const isExpanded = expandedId === order.id;

              return (
                <div className="oh-order-card" key={order.id}>
                  <div className="oh-order-head" onClick={() => setExpandedId(isExpanded ? null : order.id)}>
                    <div>
                      <div className="oh-order-id">Order #{String(order.id).padStart(5, '0')}</div>
                      <div className="oh-order-date">{new Date(order.created_at).toLocaleDateString('en-BD', { day:'numeric', month:'short', year:'numeric' })}</div>
                    </div>
                    <div className="oh-order-right">
                      <span className="oh-status-badge" style={{ color: meta.color, background: meta.bg }}>
                        {meta.label}
                      </span>
                      <span className="oh-order-total">{formatPrice(order.total_amount)}</span>
                      <span className="oh-expand-icon">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="oh-order-body">
                      <div className="oh-order-items">
                        {(order.items || []).map((item, i) => (
                          <div className="oh-order-item" key={i}>
                            {item.product_image && (
                              <img src={item.product_image} alt={item.product_name} />
                            )}
                            <div>
                              <div className="oh-item-name">{item.product_name}</div>
                              <div className="oh-item-meta">
                                Qty: {item.quantity} × {formatPrice(item.price)}
                              </div>
                            </div>
                            <div className="oh-item-sub">{formatPrice(item.price * item.quantity)}</div>
                          </div>
                        ))}
                      </div>

                      <div className="oh-order-info">
                        <div><strong>Address:</strong> {order.customer_address}, {order.customer_district}</div>
                        <div><strong>Payment:</strong> {order.payment_method === 'cod' ? 'Cash on Delivery' : order.payment_method}</div>
                        {order.note && <div><strong>Note:</strong> {order.note}</div>}
                      </div>

                      {canCancel && (
                        <button
                          className="oh-cancel-btn"
                          onClick={() => handleCancel(order.id)}
                          disabled={cancellingId === order.id}
                        >
                          {cancellingId === order.id ? 'Cancelling...' : 'Cancel Order'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;
